import { z } from "zod";
import { dateOnlySchema, emailSchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { addressSchema, blankToNull, countryCodeSchema, csvArraySchema, httpUrlSchema, isUnique, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { gstinSchema, gstStateCodeSchema, panSchema } from "./india.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * CLIENTS MODULE CONTRACT — companies, contacts, client accounts, engagements
 * and the canonical industry taxonomy.
 *   /api/v1/companies, /api/v1/contacts, /api/v1/clients,
 *   /api/v1/clients/:id/engagements, /api/v1/industries
 * Permissions: reads need `clients:read`; writes need `clients:write`;
 * archiving and merging need `clients:delete`.
 *
 * VOCABULARY (PRD §4.1 — do not confuse these):
 *  · the TENANT is an "org" in code and a "Workspace" in the UI,
 *  · a **Company** is an external business the agency sells to (never called an
 *    organization in code),
 *  · a **Contact** is a person at one or more companies,
 *  · a **Client** is the account relationship with a company after the first win
 *    (one per company, PRD I-1), and
 *  · an **Engagement** is a commercial arrangement under a client.
 * Invoices, contracts, engagements and portal access hang off `clientId`, never
 * off a lead.
 */

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

/** A company is either a registered business or an individual / sole proprietor (PRD §4.3). */
export const COMPANY_KINDS = ["company", "individual"] as const;
export type CompanyKind = (typeof COMPANY_KINDS)[number];
export const companyKindSchema = z.enum(COMPANY_KINDS);

export const COMPANY_SIZE_BANDS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"] as const;
export type CompanySizeBand = (typeof COMPANY_SIZE_BANDS)[number];
export const companySizeBandSchema = z.enum(COMPANY_SIZE_BANDS);

export const CONTACT_CHANNELS = ["whatsapp", "email", "call"] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];
export const contactChannelSchema = z.enum(CONTACT_CHANNELS);

/** What a person does for a company — several may apply at once (PRD US-2.2). */
export const COMPANY_CONTACT_ROLES = ["decision_maker", "day_to_day", "approver", "billing", "other"] as const;
export type CompanyContactRole = (typeof COMPANY_CONTACT_ROLES)[number];
export const companyContactRoleSchema = z.enum(COMPANY_CONTACT_ROLES);

/** Client account lifecycle (PRD §4.8). `health` is a separate derived signal and is never merged into this. */
export const CLIENT_STATUSES = ["onboarding", "active", "paused", "churned"] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];
export const clientStatusSchema = z.enum(CLIENT_STATUSES);

/** The ONLY transitions the API accepts (PRD §4.8). Anything else answers 409. */
export const CLIENT_STATUS_TRANSITIONS: Record<ClientStatus, readonly ClientStatus[]> = {
  onboarding: ["active", "churned"],
  active: ["paused", "churned"],
  paused: ["active", "churned"],
  churned: ["onboarding"],
};

export function canTransitionClientStatus(from: ClientStatus, to: ClientStatus): boolean {
  return CLIENT_STATUS_TRANSITIONS[from].includes(to);
}

export const CLIENT_HEALTH = ["green", "amber", "red"] as const;
export type ClientHealth = (typeof CLIENT_HEALTH)[number];
export const clientHealthSchema = z.enum(CLIENT_HEALTH);

/** Churn reasons (PRD §4.8). `regretted` is the default the dialog pre-selects; the user may override it. */
export const CHURN_REASONS = [
  { code: "BUDGET_CUT", label: "Budget cut", regretted: true },
  { code: "RESULTS", label: "Unhappy with results", regretted: true },
  { code: "SERVICE_QUALITY", label: "Unhappy with service/quality", regretted: true },
  { code: "MOVED_IN_HOUSE", label: "Moved work in-house", regretted: true },
  { code: "COMPETITOR", label: "Moved to a competitor", regretted: true },
  { code: "RELATIONSHIP", label: "Relationship broke down", regretted: true },
  { code: "ENGAGEMENT_COMPLETED", label: "Engagement completed", regretted: false },
  { code: "BUSINESS_CLOSED", label: "Client business closed", regretted: false },
  { code: "NON_PAYMENT", label: "We terminated for non-payment", regretted: false },
  { code: "OTHER", label: "Other", regretted: false },
] as const;
export type ChurnReasonCode = (typeof CHURN_REASONS)[number]["code"];
export const churnReasonCodeSchema = z.enum(CHURN_REASONS.map((r) => r.code) as [ChurnReasonCode, ...ChurnReasonCode[]]);

/** Whether losing this client is regretted, by default, for the churn report split. */
export function churnRegrettedByDefault(code: ChurnReasonCode): boolean {
  return CHURN_REASONS.find((r) => r.code === code)?.regretted ?? false;
}

export const ENGAGEMENT_TYPES = ["retainer", "fixed_fee", "time_and_materials"] as const;
export type EngagementType = (typeof ENGAGEMENT_TYPES)[number];
export const engagementTypeSchema = z.enum(ENGAGEMENT_TYPES);

export const ENGAGEMENT_STATUSES = ["draft", "active", "paused", "completed", "cancelled"] as const;
export type EngagementStatus = (typeof ENGAGEMENT_STATUSES)[number];
export const engagementStatusSchema = z.enum(ENGAGEMENT_STATUSES);

export const BILLING_FREQUENCIES = ["monthly", "quarterly", "half_yearly", "yearly", "milestone"] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];
export const billingFrequencySchema = z.enum(BILLING_FREQUENCIES);

/** How many times a year a frequency bills — `milestone` is irregular, so it contributes nothing to MRR. */
export const BILLING_PERIODS_PER_YEAR: Record<BillingFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  half_yearly: 2,
  yearly: 1,
  milestone: 0,
};

export const INVOICE_DELIVERY_METHODS = ["email", "whatsapp", "portal"] as const;
export type InvoiceDeliveryMethod = (typeof INVOICE_DELIVERY_METHODS)[number];
export const invoiceDeliveryMethodSchema = z.enum(INVOICE_DELIVERY_METHODS);

export const DELIVERABLE_ROLLOVER_POLICIES = ["none", "one_period", "unlimited"] as const;
export type DeliverableRolloverPolicy = (typeof DELIVERABLE_ROLLOVER_POLICIES)[number];
export const deliverableRolloverPolicySchema = z.enum(DELIVERABLE_ROLLOVER_POLICIES);

/** Workspace defaults for a new client's billing profile (India-first, PRD §5.1). */
export const CLIENT_BILLING_DEFAULTS = {
  paymentTermsDays: 15,
  tdsRateBps: 0,
  poRequired: false,
  invoiceDelivery: "email" as InvoiceDeliveryMethod,
} as const;

// ---------------------------------------------------------------------------
// Industry taxonomy (PRD §4.4) — seeded per workspace, lives on the COMPANY
// ---------------------------------------------------------------------------

/**
 * The canonical two-level list every workspace is seeded with. Codes are stable
 * across workspaces so curated template packs and benchmarks line up; a
 * workspace may rename or add entries and hide canonical ones, but never delete
 * a canonical code.
 */
export const INDUSTRY_TAXONOMY = [
  {
    code: "REAL_ESTATE",
    name: "Real estate",
    children: [
      { code: "RESIDENTIAL_DEVELOPER", name: "Residential developer" },
      { code: "COMMERCIAL", name: "Commercial" },
      { code: "CO_WORKING", name: "Co-working" },
      { code: "BROKERAGE", name: "Brokerage / Channel partner" },
      { code: "INTERIORS", name: "Interiors" },
    ],
  },
  {
    code: "FASHION",
    name: "Fashion",
    children: [
      { code: "D2C_APPAREL", name: "D2C apparel" },
      { code: "ETHNIC_BRIDAL", name: "Ethnic / Bridal wear" },
      { code: "JEWELLERY", name: "Jewellery" },
      { code: "FOOTWEAR_ACCESSORIES", name: "Footwear & accessories" },
      { code: "MARKETPLACE_SELLER", name: "Marketplace seller" },
    ],
  },
  {
    code: "FOOD_BEVERAGE",
    name: "Food & beverage",
    children: [
      { code: "RESTAURANT_CAFE", name: "Restaurant / Café" },
      { code: "QSR_CHAIN", name: "QSR chain" },
      { code: "CLOUD_KITCHEN", name: "Cloud kitchen" },
      { code: "PACKAGED_FOOD", name: "Packaged food" },
      { code: "BAKERY_SWEETS", name: "Bakery & sweets" },
      { code: "BAR_BREWERY", name: "Bar / Brewery" },
    ],
  },
  {
    code: "HEALTHCARE",
    name: "Healthcare",
    children: [
      { code: "HOSPITAL", name: "Hospital" },
      { code: "CLINIC_DOCTOR", name: "Clinic / Doctor" },
      { code: "DIAGNOSTICS", name: "Diagnostics" },
      { code: "PHARMA", name: "Pharma" },
      { code: "WELLNESS", name: "Wellness" },
    ],
  },
  {
    code: "EDUCATION",
    name: "Education",
    children: [
      { code: "SCHOOL", name: "School" },
      { code: "COACHING_TEST_PREP", name: "Coaching / Test prep" },
      { code: "HIGHER_EDUCATION", name: "Higher education" },
      { code: "EDTECH", name: "EdTech" },
    ],
  },
  {
    code: "BFSI",
    name: "BFSI",
    children: [
      { code: "BANK_NBFC", name: "Bank / NBFC" },
      { code: "INSURANCE", name: "Insurance" },
      { code: "WEALTH_BROKING", name: "Wealth / Broking" },
      { code: "FINTECH", name: "Fintech" },
    ],
  },
  {
    code: "BEAUTY_PERSONAL_CARE",
    name: "Beauty & personal care",
    children: [
      { code: "SALON_SPA", name: "Salon / Spa" },
      { code: "COSMETICS_BRAND", name: "Cosmetics brand" },
      { code: "DERMATOLOGY_AESTHETICS", name: "Dermatology & aesthetics" },
    ],
  },
  {
    code: "HOSPITALITY_TRAVEL",
    name: "Hospitality & travel",
    children: [
      { code: "HOTEL_RESORT", name: "Hotel / Resort" },
      { code: "TRAVEL_AGENCY", name: "Travel agency" },
      { code: "EVENTS_BANQUETS", name: "Events & banquets" },
    ],
  },
  {
    code: "AUTOMOTIVE",
    name: "Automotive",
    children: [
      { code: "DEALERSHIP", name: "Dealership" },
      { code: "EV_MOBILITY", name: "EV & mobility" },
      { code: "AUTO_COMPONENTS", name: "Auto components" },
    ],
  },
  {
    code: "TECH_SAAS",
    name: "Technology & SaaS",
    children: [
      { code: "B2B_SAAS", name: "B2B SaaS" },
      { code: "CONSUMER_APP", name: "Consumer app" },
      { code: "IT_SERVICES", name: "IT services" },
      { code: "AI_DEEPTECH", name: "AI & deeptech" },
    ],
  },
  {
    code: "MANUFACTURING_B2B",
    name: "Manufacturing & B2B",
    children: [
      { code: "INDUSTRIAL", name: "Industrial goods" },
      { code: "CHEMICALS", name: "Chemicals" },
      { code: "TEXTILES", name: "Textiles" },
      { code: "EXPORTS", name: "Exports" },
    ],
  },
  {
    code: "RETAIL",
    name: "Retail",
    children: [
      { code: "D2C_ECOMMERCE", name: "D2C e-commerce" },
      { code: "MULTI_BRAND_STORE", name: "Multi-brand store" },
      { code: "SUPERMARKET", name: "Supermarket" },
    ],
  },
  {
    code: "MEDIA_ENTERTAINMENT",
    name: "Media & entertainment",
    children: [
      { code: "PRODUCTION_HOUSE", name: "Production house" },
      { code: "PUBLISHER", name: "Publisher" },
      { code: "MUSIC_LABEL", name: "Music label" },
      { code: "GAMING", name: "Gaming" },
    ],
  },
  {
    code: "NGO",
    name: "NGO & social",
    children: [
      { code: "FOUNDATION", name: "Foundation" },
      { code: "CSR_ARM", name: "CSR arm" },
      { code: "ADVOCACY", name: "Advocacy" },
    ],
  },
  {
    code: "GOVERNMENT_PSU",
    name: "Government & PSU",
    children: [
      { code: "CENTRAL", name: "Central government" },
      { code: "STATE_MUNICIPAL", name: "State / Municipal" },
      { code: "PSU", name: "PSU" },
    ],
  },
  {
    code: "PERSONAL_BRAND",
    name: "Personal brand",
    children: [
      { code: "CREATOR_INFLUENCER", name: "Creator / Influencer" },
      { code: "PROFESSIONAL", name: "Professional (doctor, lawyer, CA)" },
      { code: "POLITICIAN", name: "Politician" },
      { code: "ARTIST", name: "Artist" },
    ],
  },
  {
    code: "OTHER",
    name: "Other",
    children: [{ code: "OTHER", name: "Other" }],
  },
] as const;

export type CanonicalIndustryCode = (typeof INDUSTRY_TAXONOMY)[number]["code"];

/** One node of the workspace's industry list. `parentId` is null for a top-level industry. */
export const industrySchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  parentId: uuidSchema.nullable(),
  isCanonical: z.boolean(),
  position: z.number().int(),
  hidden: z.boolean(),
});
export type IndustryDto = z.infer<typeof industrySchema>;

/** Compact industry reference embedded in company and client payloads. */
export const industryRefSchema = z.object({ id: uuidSchema, code: z.string(), name: z.string() });
export type IndustryRef = z.infer<typeof industryRefSchema>;

/** GET /api/v1/industries (perm `clients:read`) → the whole flat taxonomy, parents before children. */
export const listIndustriesQuerySchema = z.object({ includeHidden: queryBooleanSchema.optional() });
export type ListIndustriesQuery = z.infer<typeof listIndustriesQuerySchema>;

// ---------------------------------------------------------------------------
// Shared scalars
// ---------------------------------------------------------------------------

/** Phone numbers are stored in E.164 so WhatsApp deep links and imports agree. */
export const e164Schema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{6,14}$/, "Use the international format, e.g. +919876543210");

/** An email on a contact card. Exactly one is `isPrimary`. */
export const contactEmailSchema = z.object({ email: z.string(), label: z.string().nullable(), isPrimary: z.boolean() });
export type ContactEmail = z.infer<typeof contactEmailSchema>;

/** A phone on a contact card. `isWhatsapp` drives the click-to-chat action (PRD §5.5). */
export const contactPhoneSchema = z.object({ phone: z.string(), label: z.string().nullable(), isWhatsapp: z.boolean(), isPrimary: z.boolean() });
export type ContactPhone = z.infer<typeof contactPhoneSchema>;

/** Proof of consent for DPDP: when, from where and which notice text version (PRD §5.6). */
export const consentRecordSchema = z.object({
  optedIn: z.boolean(),
  at: timestampSchema,
  source: z.string(),
  textVersion: z.string(),
});
export type ConsentRecord = z.infer<typeof consentRecordSchema>;

export const contactConsentsSchema = z.object({
  whatsapp: consentRecordSchema.nullable(),
  marketing: consentRecordSchema.nullable(),
});
export type ContactConsents = z.infer<typeof contactConsentsSchema>;

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------

export const companyRefSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  legalName: z.string().nullable(),
  kind: companyKindSchema,
});
export type CompanyRef = z.infer<typeof companyRefSchema>;

export const contactRefSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  designation: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type ContactRef = z.infer<typeof contactRefSchema>;

export const clientRefSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  displayName: z.string(),
  status: clientStatusSchema,
});
export type ClientRef = z.infer<typeof clientRefSchema>;

// ---------------------------------------------------------------------------
// Tax registrations
// ---------------------------------------------------------------------------

/** One GSTIN of a company (a multi-state buyer has several; exactly one is the default). */
export const taxRegistrationSchema = z.object({
  id: uuidSchema,
  companyId: uuidSchema,
  gstin: z.string(),
  legalName: z.string().nullable(),
  address: addressSchema,
  stateCode: z.string(),
  pan: z.string().nullable(),
  isDefault: z.boolean(),
  ...timestampsShape,
});
export type TaxRegistrationDto = z.infer<typeof taxRegistrationSchema>;

const taxRegistrationWritableShape = {
  gstin: gstinSchema,
  legalName: blankToNull(z.string().max(200)),
  address: addressSchema,
  pan: blankToNull(panSchema),
  isDefault: z.boolean(),
};

/**
 * POST /api/v1/companies/:id/tax-registrations (perm `clients:write`) → 201 TaxRegistration.
 * The GSTIN checksum is validated and its state code is derived from the first
 * two digits. A GSTIN already registered in the workspace → CONFLICT.
 */
export const createTaxRegistrationInputSchema = z
  .object({
    ...taxRegistrationWritableShape,
    legalName: taxRegistrationWritableShape.legalName.optional(),
    address: taxRegistrationWritableShape.address.optional(),
    pan: taxRegistrationWritableShape.pan.optional(),
    isDefault: taxRegistrationWritableShape.isDefault.optional(),
  })
  .strict();
export type CreateTaxRegistrationInput = z.input<typeof createTaxRegistrationInputSchema>;

/** PATCH /api/v1/tax-registrations/:id (perm `clients:write`) → TaxRegistration. Merge patch. */
export const updateTaxRegistrationInputSchema = z.object(taxRegistrationWritableShape).partial().strict();
export type UpdateTaxRegistrationInput = z.input<typeof updateTaxRegistrationInputSchema>;

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export const companySchema = z.object({
  id: uuidSchema,
  kind: companyKindSchema,
  displayName: z.string(),
  legalName: z.string().nullable(),
  parent: companyRefSchema.nullable(),
  primaryIndustry: industryRefSchema.nullable(),
  subIndustry: industryRefSchema.nullable(),
  website: z.string().nullable(),
  domain: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  country: z.string(),
  sizeBand: companySizeBandSchema.nullable(),
  firstTouchSource: z.string().nullable(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  taxRegistrations: z.array(taxRegistrationSchema),
  /** The account relationship, when this company has become a client (PRD I-1: at most one). */
  client: clientRefSchema.nullable(),
  contactCount: z.number().int(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type CompanyDto = z.infer<typeof companySchema>;

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const companyWritableShape = {
  kind: companyKindSchema,
  displayName: z.string().trim().min(1).max(200),
  legalName: blankToNull(z.string().max(200)),
  parentCompanyId: uuidSchema.nullable(),
  primaryIndustryId: uuidSchema.nullable(),
  subIndustryId: uuidSchema.nullable(),
  website: blankToNull(httpUrlSchema),
  /** Bare host, used for deduplication and inbound email matching. */
  domain: blankToNull(
    z
      .string()
      .max(253)
      .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i, "Enter a domain like acme.com"),
  ),
  city: blankToNull(z.string().max(100)),
  stateCode: gstStateCodeSchema.nullable(),
  country: countryCodeSchema,
  sizeBand: companySizeBandSchema.nullable(),
  firstTouchSource: blankToNull(z.string().max(80)),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/companies (perm `clients:write`) → 201 Company.
 * A domain already used by another live company → CONFLICT.
 */
export const createCompanyInputSchema = z
  .object({
    ...companyWritableShape,
    kind: companyWritableShape.kind.optional(),
    legalName: companyWritableShape.legalName.optional(),
    parentCompanyId: companyWritableShape.parentCompanyId.optional(),
    primaryIndustryId: companyWritableShape.primaryIndustryId.optional(),
    subIndustryId: companyWritableShape.subIndustryId.optional(),
    website: companyWritableShape.website.optional(),
    domain: companyWritableShape.domain.optional(),
    city: companyWritableShape.city.optional(),
    stateCode: companyWritableShape.stateCode.optional(),
    country: companyWritableShape.country.optional(),
    sizeBand: companyWritableShape.sizeBand.optional(),
    firstTouchSource: companyWritableShape.firstTouchSource.optional(),
    tags: companyWritableShape.tags.optional(),
    customFields: companyWritableShape.customFields.optional(),
    /** Optional first GSTIN, so the conversion wizard can create both in one call. */
    taxRegistration: createTaxRegistrationInputSchema.optional(),
  })
  .strict();
export type CreateCompanyInput = z.input<typeof createCompanyInputSchema>;

/** PATCH /api/v1/companies/:id (perm `clients:write`) → Company. Merge patch; omitted keys unchanged. */
export const updateCompanyInputSchema = z.object(companyWritableShape).partial().strict();
export type UpdateCompanyInput = z.input<typeof updateCompanyInputSchema>;

export const listCompaniesQuerySchema = listQuerySchema.extend({
  kind: companyKindSchema.optional(),
  industryId: uuidSchema.optional(),
  parentCompanyId: uuidSchema.optional(),
  stateCode: gstStateCodeSchema.optional(),
  tags: csvArraySchema(z.string().trim().min(1).max(40), 10).optional(),
  /** `true` = only companies that already have a client account, `false` = only those that do not. */
  hasClient: queryBooleanSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["displayName", "createdAt", "updatedAt"]).optional(),
});
export type ListCompaniesQuery = z.infer<typeof listCompaniesQuerySchema>;

/**
 * POST /api/v1/companies/:id/merge (perm `clients:delete`) → the surviving Company.
 * `:id` survives, `sourceId` is archived and every link is re-pointed. Blocked
 * with CONFLICT when both companies have a client account (PRD US-2.7) — link
 * them as parent/child instead. Cannot be undone; both records are snapshotted
 * into the audit row first.
 */
export const mergeCompaniesInputSchema = z
  .object({
    sourceId: uuidSchema,
    /** Field-by-field survivors; anything omitted keeps the target's value. */
    keepFromSource: z.array(z.enum(["legalName", "website", "domain", "city", "stateCode", "country", "sizeBand", "primaryIndustryId", "subIndustryId", "firstTouchSource"])).max(12).optional(),
  })
  .strict();
export type MergeCompaniesInput = z.input<typeof mergeCompaniesInputSchema>;

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

/** A person's link to one company, with the roles they play there (PRD §4.3 ORG_CONTACT). */
export const companyContactLinkSchema = z.object({
  id: uuidSchema,
  companyId: uuidSchema,
  company: companyRefSchema,
  contactId: uuidSchema,
  contact: contactRefSchema,
  title: z.string().nullable(),
  roles: z.array(companyContactRoleSchema),
  isPrimary: z.boolean(),
  activeFrom: dateOnlySchema.nullable(),
  activeTo: dateOnlySchema.nullable(),
  ...timestampsShape,
});
export type CompanyContactLinkDto = z.infer<typeof companyContactLinkSchema>;

export const contactSchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  fullName: z.string(),
  designation: z.string().nullable(),
  emails: z.array(contactEmailSchema),
  phones: z.array(contactPhoneSchema),
  preferredChannel: contactChannelSchema.nullable(),
  consents: contactConsentsSchema,
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  /** Every company this person works with — a contact may serve several (PRD US-2.2). */
  companies: z.array(companyContactLinkSchema),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ContactDto = z.infer<typeof contactSchema>;

const contactEmailInputSchema = z.object({ email: emailSchema, label: blankToNull(z.string().max(40)).optional(), isPrimary: z.boolean().optional() }).strict();
const contactPhoneInputSchema = z
  .object({ phone: e164Schema, label: blankToNull(z.string().max(40)).optional(), isWhatsapp: z.boolean().optional(), isPrimary: z.boolean().optional() })
  .strict();
const consentInputSchema = z
  .object({ optedIn: z.boolean(), source: z.string().trim().min(1).max(120), textVersion: z.string().trim().min(1).max(40), at: timestampSchema.optional() })
  .strict();

const contactWritableShape = {
  firstName: z.string().trim().min(1).max(100),
  lastName: blankToNull(z.string().max(100)),
  designation: blankToNull(z.string().max(120)),
  /** Replaced as a whole. At most one entry may be `isPrimary`; the first one wins otherwise. */
  emails: z.array(contactEmailInputSchema).max(10).refine((list) => isUnique(list.map((e) => e.email.toLowerCase())), { message: "Duplicate email address" }),
  /** Replaced as a whole. */
  phones: z.array(contactPhoneInputSchema).max(10).refine((list) => isUnique(list.map((p) => p.phone)), { message: "Duplicate phone number" }),
  preferredChannel: contactChannelSchema.nullable(),
  /** Merged key by key; each entry is stamped with the time it was recorded. */
  consents: z.object({ whatsapp: consentInputSchema.nullable().optional(), marketing: consentInputSchema.nullable().optional() }).strict(),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/** POST /api/v1/contacts (perm `clients:write`) → 201 Contact. */
export const createContactInputSchema = z
  .object({
    ...contactWritableShape,
    lastName: contactWritableShape.lastName.optional(),
    designation: contactWritableShape.designation.optional(),
    emails: contactWritableShape.emails.optional(),
    phones: contactWritableShape.phones.optional(),
    preferredChannel: contactWritableShape.preferredChannel.optional(),
    consents: contactWritableShape.consents.optional(),
    tags: contactWritableShape.tags.optional(),
    customFields: contactWritableShape.customFields.optional(),
    /** Optionally link the new person to a company in the same call. */
    companyId: uuidSchema.optional(),
    title: blankToNull(z.string().max(120)).optional(),
    roles: z.array(companyContactRoleSchema).max(5).optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();
export type CreateContactInput = z.input<typeof createContactInputSchema>;

/** PATCH /api/v1/contacts/:id (perm `clients:write`) → Contact. Merge patch; `emails`/`phones` are replaced as a whole. */
export const updateContactInputSchema = z.object(contactWritableShape).partial().strict();
export type UpdateContactInput = z.input<typeof updateContactInputSchema>;

export const listContactsQuerySchema = listQuerySchema.extend({
  companyId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  role: companyContactRoleSchema.optional(),
  tags: csvArraySchema(z.string().trim().min(1).max(40), 10).optional(),
  whatsappOptIn: queryBooleanSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["name", "createdAt", "updatedAt"]).optional(),
});
export type ListContactsQuery = z.infer<typeof listContactsQuerySchema>;

/** POST /api/v1/contacts/:id/links (perm `clients:write`) → 201 CompanyContactLink. Re-linking an unlinked pair revives the existing row. */
export const linkContactInputSchema = z
  .object({
    companyId: uuidSchema,
    title: blankToNull(z.string().max(120)).optional(),
    roles: z.array(companyContactRoleSchema).max(5).optional(),
    isPrimary: z.boolean().optional(),
    activeFrom: dateOnlySchema.nullable().optional(),
  })
  .strict();
export type LinkContactInput = z.input<typeof linkContactInputSchema>;

/** PATCH /api/v1/contacts/:id/links/:linkId (perm `clients:write`) → CompanyContactLink. */
export const updateContactLinkInputSchema = z
  .object({
    title: blankToNull(z.string().max(120)),
    roles: z.array(companyContactRoleSchema).max(5),
    isPrimary: z.boolean(),
    activeFrom: dateOnlySchema.nullable(),
    activeTo: dateOnlySchema.nullable(),
  })
  .partial()
  .strict();
export type UpdateContactLinkInput = z.input<typeof updateContactLinkInputSchema>;

/**
 * POST /api/v1/contacts/:id/merge (perm `clients:delete`) → the surviving Contact.
 * `:id` survives; the source's emails, phones, consents, tags and company links
 * move across and the source is archived. Cannot be undone; audited with a
 * snapshot of both records.
 */
export const mergeContactsInputSchema = z.object({ sourceId: uuidSchema }).strict();
export type MergeContactsInput = z.input<typeof mergeContactsInputSchema>;

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

/** How this client is billed. Editing it never changes an issued document (PRD US-2.3). */
export const clientBillingProfileSchema = z.object({
  defaultTaxRegistrationId: uuidSchema.nullable(),
  billingContactIds: z.array(uuidSchema),
  billingCurrency: currencyCodeSchema,
  paymentTermsDays: z.number().int(),
  tdsRateBps: z.number().int(),
  tdsLabel: z.string().nullable(),
  poRequired: z.boolean(),
  invoiceDelivery: invoiceDeliveryMethodSchema,
  ccEmails: z.array(z.string()),
});
export type ClientBillingProfile = z.infer<typeof clientBillingProfileSchema>;

export const clientSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  company: companyRefSchema,
  displayName: z.string(),
  status: clientStatusSchema,
  statusChangedAt: timestampSchema,
  clientSince: dateOnlySchema.nullable(),
  reactivatedAt: timestampSchema.nullable(),
  accountManager: userRefSchema.nullable(),
  backupAm: userRefSchema.nullable(),
  team: z.array(userRefSchema),
  /** The lead this account came from, when it was converted rather than created by hand. */
  originLeadId: uuidSchema.nullable(),
  billing: clientBillingProfileSchema,
  portalEnabled: z.boolean(),
  pauseReason: z.string().nullable(),
  resumeOn: dateOnlySchema.nullable(),
  churnReasonCode: churnReasonCodeSchema.nullable(),
  churnedAt: timestampSchema.nullable(),
  churnRegretted: z.boolean().nullable(),
  churnNotes: z.string().nullable(),
  /** Derived signal (Phase 2+), deliberately separate from `status`. */
  health: clientHealthSchema.nullable(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ClientDto = z.infer<typeof clientSchema>;

const billingProfileInputShape = {
  defaultTaxRegistrationId: uuidSchema.nullable(),
  billingContactIds: z.array(uuidSchema).max(10),
  billingCurrency: currencyCodeSchema,
  paymentTermsDays: z.number().int().min(0).max(365),
  tdsRateBps: z.number().int().min(0).max(10000),
  tdsLabel: blankToNull(z.string().max(60)),
  poRequired: z.boolean(),
  invoiceDelivery: invoiceDeliveryMethodSchema,
  ccEmails: z.array(emailSchema).max(10),
};
/** Merged key by key onto the stored profile. */
export const clientBillingProfileInputSchema = z.object(billingProfileInputShape).partial().strict();
export type ClientBillingProfileInput = z.input<typeof clientBillingProfileInputSchema>;

const clientWritableShape = {
  displayName: z.string().trim().min(1).max(200),
  accountManagerId: uuidSchema,
  backupAmId: uuidSchema.nullable(),
  team: z.array(uuidSchema).max(50),
  clientSince: dateOnlySchema.nullable(),
  billing: clientBillingProfileInputSchema,
  portalEnabled: z.boolean(),
  health: clientHealthSchema.nullable(),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/clients (perm `clients:write`) → 201 Client.
 * The company must not already have a client account (PRD I-1) → CONFLICT.
 * The account manager must be an active member of the workspace.
 */
export const createClientInputSchema = z
  .object({
    ...clientWritableShape,
    companyId: uuidSchema,
    displayName: clientWritableShape.displayName.optional(),
    backupAmId: clientWritableShape.backupAmId.optional(),
    team: clientWritableShape.team.optional(),
    clientSince: clientWritableShape.clientSince.optional(),
    billing: clientWritableShape.billing.optional(),
    portalEnabled: clientWritableShape.portalEnabled.optional(),
    health: clientWritableShape.health.optional(),
    tags: clientWritableShape.tags.optional(),
    customFields: clientWritableShape.customFields.optional(),
    /** Optional: `onboarding` (the default) or `active` for imported accounts. */
    status: z.enum(["onboarding", "active"]).optional(),
    originLeadId: uuidSchema.optional(),
  })
  .strict();
export type CreateClientInput = z.input<typeof createClientInputSchema>;

/** PATCH /api/v1/clients/:id (perm `clients:write`) → Client. `status` moves only through POST /clients/:id/status. */
export const updateClientInputSchema = z.object(clientWritableShape).partial().strict();
export type UpdateClientInput = z.input<typeof updateClientInputSchema>;

export const listClientsQuerySchema = listQuerySchema.extend({
  status: csvArraySchema(clientStatusSchema, CLIENT_STATUSES.length).optional(),
  industryId: uuidSchema.optional(),
  accountManagerId: uuidSchema.optional(),
  companyId: uuidSchema.optional(),
  tags: csvArraySchema(z.string().trim().min(1).max(40), 10).optional(),
  portalEnabled: queryBooleanSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["displayName", "number", "clientSince", "createdAt", "updatedAt"]).optional(),
});
export type ListClientsQuery = z.infer<typeof listClientsQuerySchema>;

/**
 * POST /api/v1/clients/:id/status (perm `clients:write`) → Client.
 * Only the transitions in PRD §4.8 are accepted (anything else → CONFLICT) and
 * each carries its own required fields: pausing needs a reason, churning needs a
 * reason code. Reactivating keeps `clientSince` and sets `reactivatedAt`.
 */
export const changeClientStatusInputSchema = z
  .object({
    status: clientStatusSchema,
    /** Required for → paused. */
    pauseReason: z.string().trim().min(1).max(500).optional(),
    resumeOn: dateOnlySchema.nullable().optional(),
    /** Required for → churned. */
    churnReasonCode: churnReasonCodeSchema.optional(),
    churnRegretted: z.boolean().optional(),
    churnNotes: blankToNull(z.string().max(2000)).optional(),
    /** Churn effective date; defaults to today. */
    effectiveDate: dateOnlySchema.optional(),
    note: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type ChangeClientStatusInput = z.input<typeof changeClientStatusInputSchema>;

/** One row of the client's status ledger — every transition, with who and why. */
export const clientStatusHistorySchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  fromStatus: clientStatusSchema.nullable(),
  toStatus: clientStatusSchema,
  reasonCode: z.string().nullable(),
  reason: z.string().nullable(),
  effectiveDate: dateOnlySchema.nullable(),
  actorType: z.string(),
  actorId: uuidSchema.nullable(),
  actorName: z.string(),
  createdAt: timestampSchema,
});
export type ClientStatusHistoryDto = z.infer<typeof clientStatusHistorySchema>;

/**
 * GET /api/v1/clients/:id/summary (perm `clients:read`) → the 360 header
 * (PRD US-2.1). `mrrMinor` is the monthly-equivalent fee of the active retainer
 * engagements; `outstandingMinor` / `overdueMinor` stay 0 until the invoices
 * module lands.
 */
export const clientSummarySchema = z.object({
  id: uuidSchema,
  number: z.string(),
  displayName: z.string(),
  legalName: z.string().nullable(),
  company: companyRefSchema,
  status: clientStatusSchema,
  statusChangedAt: timestampSchema,
  primaryIndustry: industryRefSchema.nullable(),
  subIndustry: industryRefSchema.nullable(),
  accountManager: userRefSchema.nullable(),
  clientSince: dateOnlySchema.nullable(),
  currency: currencyCodeSchema,
  /** Masked to `null` — never 0 — for a role without `financials:view`. */
  mrrMinor: minorSchema.nullable(),
  activeEngagements: z.number().int(),
  /** Open receivables on this account. Masked to `null` without `financials:view`. */
  outstandingMinor: minorSchema.nullable(),
  overdueMinor: minorSchema.nullable(),
  contactCount: z.number().int(),
  primaryContact: contactRefSchema.nullable(),
  portalEnabled: z.boolean(),
  health: clientHealthSchema.nullable(),
  tags: z.array(z.string()),
  lastActivityAt: timestampSchema.nullable(),
});
export type ClientSummaryDto = z.infer<typeof clientSummarySchema>;

// ---------------------------------------------------------------------------
// Engagements
// ---------------------------------------------------------------------------

export const billingScheduleSchema = z.object({
  frequency: billingFrequencySchema,
  /** Day of the billing period the invoice is raised on (1–28), null for milestone billing. */
  dayOfPeriod: z.number().int().nullable(),
  /** `advance` bills at the start of the period, `arrears` at the end. */
  timing: z.enum(["advance", "arrears"]),
});
export type BillingSchedule = z.infer<typeof billingScheduleSchema>;

export const deliverableQuotaSchema = z.object({
  type: z.string(),
  qty: z.number().int(),
  rollover: deliverableRolloverPolicySchema,
});
export type DeliverableQuota = z.infer<typeof deliverableQuotaSchema>;

export const engagementSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  type: engagementTypeSchema,
  name: z.string(),
  feeMinor: minorSchema,
  currency: currencyCodeSchema,
  billingSchedule: billingScheduleSchema,
  /** Monthly-equivalent fee, so retainers of any frequency roll up into MRR. */
  monthlyValueMinor: minorSchema,
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int(),
  deliverableQuotas: z.array(deliverableQuotaSchema),
  includedRevisionRounds: z.number().int(),
  contractId: uuidSchema.nullable(),
  status: engagementStatusSchema,
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type EngagementDto = z.infer<typeof engagementSchema>;

const billingScheduleInputSchema = z
  .object({
    frequency: billingFrequencySchema,
    dayOfPeriod: z.number().int().min(1).max(28).nullable().optional(),
    timing: z.enum(["advance", "arrears"]).optional(),
  })
  .strict();

const engagementWritableShape = {
  type: engagementTypeSchema,
  name: z.string().trim().min(1).max(200),
  feeMinor: minorSchema.min(0),
  currency: currencyCodeSchema,
  billingSchedule: billingScheduleInputSchema,
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int().min(0).max(365),
  deliverableQuotas: z
    .array(z.object({ type: z.string().trim().min(1).max(80), qty: z.number().int().min(0).max(10000), rollover: deliverableRolloverPolicySchema.optional() }).strict())
    .max(30),
  includedRevisionRounds: z.number().int().min(0).max(100),
  contractId: uuidSchema.nullable(),
  status: engagementStatusSchema,
};

/**
 * POST /api/v1/clients/:id/engagements (perm `clients:write`) → 201 Engagement.
 * `endDate` before `startDate` → 422.
 */
export const createEngagementInputSchema = z
  .object({
    ...engagementWritableShape,
    feeMinor: engagementWritableShape.feeMinor.optional(),
    currency: engagementWritableShape.currency.optional(),
    billingSchedule: engagementWritableShape.billingSchedule.optional(),
    startDate: engagementWritableShape.startDate.optional(),
    endDate: engagementWritableShape.endDate.optional(),
    autoRenew: engagementWritableShape.autoRenew.optional(),
    noticeDays: engagementWritableShape.noticeDays.optional(),
    deliverableQuotas: engagementWritableShape.deliverableQuotas.optional(),
    includedRevisionRounds: engagementWritableShape.includedRevisionRounds.optional(),
    contractId: engagementWritableShape.contractId.optional(),
    status: engagementWritableShape.status.optional(),
  })
  .strict();
export type CreateEngagementInput = z.input<typeof createEngagementInputSchema>;

/** PATCH /api/v1/engagements/:id (perm `clients:write`) → Engagement. Merge patch. */
export const updateEngagementInputSchema = z.object(engagementWritableShape).partial().strict();
export type UpdateEngagementInput = z.input<typeof updateEngagementInputSchema>;

export const listEngagementsQuerySchema = listQuerySchema.extend({
  status: csvArraySchema(engagementStatusSchema, ENGAGEMENT_STATUSES.length).optional(),
  type: engagementTypeSchema.optional(),
  clientId: uuidSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["name", "startDate", "feeMinor", "createdAt"]).optional(),
});
export type ListEngagementsQuery = z.infer<typeof listEngagementsQuerySchema>;

/**
 * Monthly-equivalent value of a fee billed at `frequency`, in minor units.
 * Milestone billing is irregular, so it contributes nothing to MRR.
 */
export function monthlyValueMinor(feeMinor: number, frequency: BillingFrequency): number {
  const periods = BILLING_PERIODS_PER_YEAR[frequency];
  if (!periods) return 0;
  return Math.round((feeMinor * periods) / 12);
}

/** Display name of a client number, e.g. 42 → "C-0042". */
export function formatClientNumber(sequence: number): string {
  return `C-${String(sequence).padStart(4, "0")}`;
}

/** "Priya Sharma" from its parts, collapsing a missing surname. */
export function contactFullName(firstName: string, lastName: string | null | undefined): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}
